/**
 * WorkspaceViewDialog — READ-ONLY inspector for a shared workspace, opened by
 * workspaceId from the Connections tab (doc/arch/ASSETS.md + SKILLS.md).
 *
 * Purpose: "just for info" — see what's inside a workspace without a network/agent
 * context: the file tree (template folder, uploaded files) AND a provenance header
 * (which template it was seeded from + which tenant skills@version were baked in).
 * No edit/delete here — inspection only. Browses by workspaceId via the
 * tenant-workspace view endpoints (backend authes on the workspace's tenant).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X as XIcon, Folder, File as FileIcon, ChevronLeft, Loader2, Sparkles } from 'lucide-react';
import { viewWorkspaceTree, viewWorkspaceFile, viewWorkspaceRawUrl } from '../../services/toolsApi.js';

function _fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * @param {object} props
 * @param {string} props.workspaceId
 * @param {string} [props.name]        workspace label for the header
 * @param {object} [props.provenance]  { templateId?, skillIds?, tenantSkills?[{id,version}] }
 * @param {()=>void} props.onClose
 */
export default function WorkspaceViewDialog({ workspaceId, name, provenance, onClose }) {
  const [prefix, setPrefix] = useState('');
  const [folder, setFolder] = useState(null); // { entries, breadcrumb, cold?, displayName }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null); // { path, content?, url?, kind } | null

  const load = useCallback(async (pfx) => {
    setLoading(true);
    setError('');
    try {
      const f = await viewWorkspaceTree(workspaceId, pfx);
      setFolder(f);
    } catch (e) {
      setError(e?.message || 'Could not load workspace');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(prefix); }, [load, prefix]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const openFile = useCallback(async (entry) => {
    setSelected({ path: entry.path, loading: true });
    try {
      const f = await viewWorkspaceFile(workspaceId, entry.path);
      if (f && f.binary) {
        const { url } = await viewWorkspaceRawUrl(workspaceId, entry.path);
        setSelected({ path: entry.path, url });
      } else {
        setSelected({ path: entry.path, content: (f && (f.content ?? f.text)) || '' });
      }
    } catch (e) {
      setSelected({ path: entry.path, error: e?.message || 'Could not open file' });
    }
  }, [workspaceId]);

  const entries = folder?.entries || [];
  const cold = !loading && !error && folder?.cold;

  // Baked skills, preferring the version-stamped shape.
  const bakedSkills = (provenance?.tenantSkills && Array.isArray(provenance.tenantSkills))
    ? provenance.tenantSkills.map((s) => (typeof s === 'string' ? { id: s } : s))
    : (Array.isArray(provenance?.tenantSkillIds) ? provenance.tenantSkillIds.map((id) => ({ id })) : []);
  const importedSkills = Array.isArray(provenance?.skillIds) ? provenance.skillIds : [];

  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" data-testid="workspace-view-dialog"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900">
              Workspace · {name || workspaceId}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">Read-only view — files + what was baked in.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Provenance header */}
        {(provenance?.templateId || bakedSkills.length || importedSkills.length) ? (
          <div className="shrink-0 border-b border-gray-100 bg-gray-50/70 px-5 py-2 text-[11px] text-gray-600">
            {provenance?.templateId ? (
              <span className="mr-3">Seeded from template <span className="font-medium text-gray-800">{provenance.templateId}</span></span>
            ) : null}
            {bakedSkills.length ? (
              <span className="mr-3">
                <Sparkles className="mr-0.5 inline h-3 w-3 text-violet-600" aria-hidden />
                Skills: {bakedSkills.map((s) => `${s.id}${s.version != null ? ` v${s.version}` : ''}`).join(', ')}
              </span>
            ) : null}
            {importedSkills.length ? (
              <span>Imported: {importedSkills.join(', ')}</span>
            ) : null}
          </div>
        ) : null}

        {/* Body: tree (left) + file preview (right) */}
        <div className="flex min-h-0 flex-1">
          <div className="w-72 shrink-0 overflow-y-auto border-r border-gray-100">
            {/* breadcrumb / back */}
            {prefix ? (
              <button type="button" onClick={() => setPrefix(prefix.replace(/[^/]+\/?$/, '').replace(/\/$/, ''))}
                className="flex w-full items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-blue-600 hover:bg-blue-50/40">
                <ChevronLeft className="h-3 w-3" /> up
              </button>
            ) : null}
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-6 text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : error ? (
              <p className="px-3 py-6 text-[11px] text-red-600">{error}</p>
            ) : cold ? (
              <p className="px-3 py-6 text-[11px] text-gray-400">This workspace has no content yet.</p>
            ) : entries.length === 0 ? (
              <p className="px-3 py-6 text-[11px] text-gray-400">Empty folder.</p>
            ) : entries.map((e) => (
              <button key={e.path} type="button"
                onClick={() => (e.isDir ? setPrefix(e.path) : openFile(e))}
                data-testid={`workspace-view-entry-${e.path}`}
                className={`flex w-full items-center gap-1.5 px-3 py-1 text-left text-[11px] ${
                  !e.isDir && selected?.path === e.path ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-blue-50/40'
                }`}
                title={e.path}>
                {e.isDir
                  ? <Folder className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                  : <FileIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
                <span className="truncate">{e.name}</span>
                {!e.isDir ? <span className="ml-auto shrink-0 text-[9px] text-gray-400">{_fmtBytes(e.size)}</span> : null}
              </button>
            ))}
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            {!selected ? (
              <div className="flex flex-1 items-center justify-center p-6 text-center text-xs text-gray-400">
                Select a file to view it.
              </div>
            ) : (
              <>
                <div className="border-b border-gray-100 px-4 py-1.5">
                  <span className="truncate font-mono text-[11px] text-gray-600" title={selected.path}>{selected.path}</span>
                </div>
                <div className="min-h-0 flex-1 overflow-auto p-3">
                  {selected.loading ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Opening…</div>
                  ) : selected.error ? (
                    <p className="text-[11px] text-red-600">{selected.error}</p>
                  ) : selected.url ? (
                    <a href={selected.url} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-blue-600 hover:underline">
                      Open / download this file
                    </a>
                  ) : (
                    <pre className="whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-gray-800">{selected.content}</pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
